import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, MessageSquare, Trophy, BookOpen, Megaphone, FileText, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const NOTIF_ICONS = {
  message: MessageSquare,
  announcement: Megaphone,
  win: Trophy,
  document: FileText,
  workshop: BookOpen,
  cohort: Users,
  default: Bell
};

const NotificationCenter = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/my`, { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnread(data.unread || 0);
      }
    } catch (e) { /* notify err */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await fetch(`${API}/api/notifications/${id}/read`, { method: 'POST', headers: hdr() });
      fetchNotifications();
    } catch (e) { /* notify err */ }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/api/notifications/read-all`, { method: 'POST', headers: hdr() });
      fetchNotifications();
    } catch (e) { /* notify err */ }
  };

  const formatTime = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    return `${Math.floor(diff/86400000)}d ago`;
  };

  return (
    <div className="relative" data-testid="notification-center">
      {/* Bell button */}
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-xl hover:bg-white/10 transition-all" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-white/70" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div initial={{ opacity: 0, y: -10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute right-0 top-12 w-80 max-h-96 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-testid="notification-dropdown">
              {/* Header */}
              <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                <p className="text-sm font-bold text-gray-900">Notifications</p>
                {unread > 0 && (
                  <button onClick={markAllRead} className="text-[10px] text-orange-500 font-bold hover:underline">Mark all read</button>
                )}
              </div>
              {/* List */}
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">No notifications yet</p>
                  </div>
                ) : (
                  notifications.slice(0, 20).map(n => {
                    const Icon = NOTIF_ICONS[n.type] || NOTIF_ICONS.default;
                    return (
                      <div key={n.id} onClick={() => { markRead(n.id); if (n.link) window.location.href = n.link; }}
                        className={`p-3 flex gap-3 cursor-pointer hover:bg-gray-50 transition-all border-b border-gray-50 ${!n.read ? 'bg-orange-50/50' : ''}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${!n.read ? 'bg-orange-100' : 'bg-gray-100'}`}>
                          <Icon className={`w-4 h-4 ${!n.read ? 'text-orange-500' : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs leading-relaxed ${!n.read ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>{n.message}</p>
                          <p className="text-[9px] text-gray-400 mt-0.5">{formatTime(n.created_at)}</p>
                        </div>
                        {!n.read && <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />}
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
